import { homePathSelectLabel } from '@/pages/master/role-permission/homePathConfig';
import type { RolePermissionRole } from '@/store/modules/rolePermission';

/** Matches labels on RolePermissionEditPage / RolePermissionCreateDetail. */
export const ROLE_META_FIELD_ORDER = [
  'code',
  'name',
  'homePath',
  'homePathDraft',
  'level',
  'parentRoleIds',
  'managedChildRoleIds'
] as const;

export type RoleMetaFieldKey = (typeof ROLE_META_FIELD_ORDER)[number];

export const ROLE_META_FIELD_LABELS: Record<RoleMetaFieldKey, string> = {
  code: 'ロールコード',
  name: 'ロール名',
  homePath: 'ホーム画面',
  homePathDraft: 'ホーム画面（仮登録）',
  level: 'ロール種別',
  parentRoleIds: '親ロール',
  managedChildRoleIds: '管理対象担当者'
};

const FIELD_KEY_ALIASES: Record<string, RoleMetaFieldKey> = {
  roleCode: 'code',
  roleName: 'name',
  home_path: 'homePath',
  HOME_PATH: 'homePath',
  home_path_draft: 'homePathDraft',
  homePathDraft: 'homePathDraft',
  parent_role_ids: 'parentRoleIds',
  managed_child_role_ids: 'managedChildRoleIds',
  managedChildRoles: 'managedChildRoleIds',
  parentRoles: 'parentRoleIds'
};

const PAYLOAD_META_SKIP_KEYS = new Set([
  'permissionsDraft',
  'permissionChanges',
  'approvedPermissionChanges',
  'updatedScreens',
  'comment',
  'roleMetaChanges',
  'relatedChildRole',
  'action'
]);

export type RoleMetaChangeRow = {
  /** Stable API key (e.g. `homePath`). */
  fieldKey: RoleMetaFieldKey;
  /** Label matching RolePermissionEditPage form (`ホーム画面`, …). */
  field: string;
  oldValue: string;
  newValue: string;
  /** Set for `parentRoleIds` / `managedChildRoleIds` — render with el-tag. */
  oldTags?: string[];
  newTags?: string[];
};

const ROLE_IDS_META_KEYS = new Set<RoleMetaFieldKey>([
  'parentRoleIds',
  'managedChildRoleIds'
]);

const asRecord = (value: unknown): Record<string, unknown> | null =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;

const formatMetaCell = (rawValue: unknown): string => {
  if (rawValue === null || rawValue === undefined) return '—';
  if (
    typeof rawValue === 'string' ||
    typeof rawValue === 'number' ||
    typeof rawValue === 'boolean'
  ) {
    return String(rawValue);
  }
  try {
    return JSON.stringify(rawValue);
  } catch {
    return String(rawValue);
  }
};

const formatRoleLevel = (value: unknown): string => {
  const level = Number(value);
  if (level === 3) return 'スーパー管理者';
  if (level === 2) return '管理者';
  if (level === 1) return '担当者';
  return formatMetaCell(value);
};

export const buildRoleNameByIdMap = (
  roles: RolePermissionRole[]
): Map<number, string> => {
  const map = new Map<number, string>();
  for (const role of roles) {
    const id = Number(role.id);
    if (!Number.isFinite(id)) continue;
    const label = role.name?.trim() || role.code?.trim();
    if (label) map.set(id, label);
  }
  return map;
};

/** Resolves role id → display name from `rolePermission/roles` in store. */
export const resolveRoleDisplayName = (
  roleId: unknown,
  roles: RolePermissionRole[],
  roleNameById?: Map<number, string>
): string => {
  const id = Number(roleId);
  if (!Number.isFinite(id)) return '—';
  const cached = roleNameById?.get(id);
  if (cached) return cached;
  const matched = roles.find((candidate) => Number(candidate.id) === id);
  const label = matched?.name?.trim() || matched?.code?.trim();
  return label || '—';
};

export const formatRoleIdsToLabels = (
  value: unknown,
  roles: RolePermissionRole[]
): string[] => {
  const ids = Array.isArray(value) ? value : [];
  if (!ids.length) return [];
  const roleNameById = buildRoleNameByIdMap(roles);
  return ids
    .map((roleId) => resolveRoleDisplayName(roleId, roles, roleNameById))
    .filter((label) => label !== '—');
};

/** Labels for el-tag UI (ids, or joined display text from `formatRoleIdsList`). */
export const formatRoleMetaTags = (
  value: unknown,
  roles: RolePermissionRole[],
  displayFallback?: string
): string[] => {
  const fromIds = formatRoleIdsToLabels(value, roles);
  if (fromIds.length) return fromIds;
  const text =
    typeof value === 'string' ? value.trim() : (displayFallback?.trim() ?? '');
  if (!text || text === '—') return [];
  return text
    .split('、')
    .map((part) => part.trim())
    .filter(Boolean);
};

const formatRoleIdsList = (
  value: unknown,
  roles: RolePermissionRole[]
): string => {
  const labels = formatRoleIdsToLabels(value, roles);
  return labels.length ? labels.join('、') : '—';
};

const buildRoleMetaChangeRow = (
  key: RoleMetaFieldKey,
  from: unknown,
  to: unknown,
  roles: RolePermissionRole[]
): RoleMetaChangeRow | null => {
  const oldValue = formatRoleMetaFieldValue(key, from, roles);
  const newValue = formatRoleMetaFieldValue(key, to, roles);
  if (oldValue === newValue) return null;
  const row: RoleMetaChangeRow = {
    fieldKey: key,
    field: ROLE_META_FIELD_LABELS[key],
    oldValue,
    newValue
  };
  if (ROLE_IDS_META_KEYS.has(key)) {
    row.oldTags = formatRoleMetaTags(from, roles, oldValue);
    row.newTags = formatRoleMetaTags(to, roles, newValue);
  }
  return row;
};

const camelizeMetaKey = (rawKey: string): string =>
  rawKey.replace(/_([a-z])/g, (_, letter: string) => letter.toUpperCase());

export const normalizeRoleMetaFieldKey = (
  rawKey: string
): RoleMetaFieldKey | null => {
  const trimmed = camelizeMetaKey(rawKey.trim());
  if (!trimmed) return null;
  if (trimmed in ROLE_META_FIELD_LABELS) {
    return trimmed as RoleMetaFieldKey;
  }
  const alias = FIELD_KEY_ALIASES[trimmed];
  if (alias) return alias;
  const byLabel = (
    Object.entries(ROLE_META_FIELD_LABELS) as [RoleMetaFieldKey, string][]
  ).find(([, label]) => label === trimmed);
  return byLabel?.[0] ?? null;
};

export const formatRoleMetaFieldValue = (
  key: RoleMetaFieldKey,
  value: unknown,
  roles: RolePermissionRole[]
): string => {
  if (value === null || value === undefined) return '—';
  if (key === 'homePath' || key === 'homePathDraft') {
    const label = homePathSelectLabel(
      typeof value === 'string' ? value : String(value)
    );
    return label || '—';
  }
  if (key === 'level') return formatRoleLevel(value);
  if (key === 'parentRoleIds' || key === 'managedChildRoleIds') {
    return formatRoleIdsList(value, roles);
  }
  return formatMetaCell(value);
};

export const normalizeHistoryPayload = (
  raw: unknown
): Record<string, unknown> | null => {
  if (raw == null) return null;
  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (!trimmed) return null;
    try {
      return asRecord(JSON.parse(trimmed));
    } catch {
      return null;
    }
  }
  return asRecord(raw);
};

const extractFromToPair = (
  raw: unknown
): { from: unknown; to: unknown } | null => {
  if (Array.isArray(raw) && raw.length >= 2) {
    return { from: raw[0], to: raw[1] };
  }
  const record = asRecord(raw);
  if (!record) return null;
  if ('from' in record || 'to' in record) {
    return { from: record.from, to: record.to };
  }
  if ('before' in record || 'after' in record) {
    return { from: record.before, to: record.after };
  }
  if ('old' in record || 'new' in record) {
    return { from: record.old, to: record.new };
  }
  if ('previous' in record || 'current' in record) {
    return { from: record.previous, to: record.current };
  }
  if ('oldValue' in record || 'newValue' in record) {
    return {
      from: record.oldValue ?? record.from,
      to: record.newValue ?? record.to
    };
  }
  return null;
};

const isScalarMetaValue = (value: unknown): boolean =>
  typeof value === 'string' ||
  typeof value === 'number' ||
  typeof value === 'boolean' ||
  value === null;

const ingestBeforeAfterRoot = (
  record: Record<string, unknown>,
  roles: RolePermissionRole[],
  byKey: Map<RoleMetaFieldKey, RoleMetaChangeRow>
) => {
  const upsert = (key: RoleMetaFieldKey, from: unknown, to: unknown) => {
    const row = buildRoleMetaChangeRow(key, from, to, roles);
    if (!row) return;
    byKey.set(key, row);
  };

  const before =
    asRecord(record.before) ??
    asRecord(record.old) ??
    asRecord(record.previous);
  const after =
    asRecord(record.after) ?? asRecord(record.new) ?? asRecord(record.current);
  if (!before && !after) return;

  for (const key of ROLE_META_FIELD_ORDER) {
    const from = before?.[key];
    const to = after?.[key];
    if (from !== undefined || to !== undefined) {
      upsert(key, from, to);
    }
  }
};

const ingestSnapshotScalarDiff = (
  current: Record<string, unknown>,
  previous: Record<string, unknown> | null,
  roles: RolePermissionRole[],
  byKey: Map<RoleMetaFieldKey, RoleMetaChangeRow>
) => {
  const upsert = (key: RoleMetaFieldKey, from: unknown, to: unknown) => {
    const row = buildRoleMetaChangeRow(key, from, to, roles);
    if (!row) return;
    byKey.set(key, row);
  };

  for (const key of ROLE_META_FIELD_ORDER) {
    if (!(key in current)) continue;
    const cur = current[key];
    if (extractFromToPair(cur)) continue;
    if (!isScalarMetaValue(cur) && !Array.isArray(cur)) continue;
    const prev = previous?.[key];
    if (prev === undefined && cur === undefined) continue;
    if (JSON.stringify(prev) !== JSON.stringify(cur)) {
      upsert(key, prev, cur);
    }
  }
};

const ingestPayloadRecord = (
  record: Record<string, unknown>,
  roles: RolePermissionRole[],
  byKey: Map<RoleMetaFieldKey, RoleMetaChangeRow>
) => {
  const upsert = (key: RoleMetaFieldKey, from: unknown, to: unknown) => {
    const row = buildRoleMetaChangeRow(key, from, to, roles);
    if (!row) return;
    byKey.set(key, row);
  };

  const ingestEntry = (rawKey: string, from: unknown, to: unknown) => {
    const key = normalizeRoleMetaFieldKey(rawKey);
    if (!key) return;
    upsert(key, from, to);
  };

  for (const key of ROLE_META_FIELD_ORDER) {
    if (!(key in record)) continue;
    const pair = extractFromToPair(record[key]);
    if (pair) upsert(key, pair.from, pair.to);
  }

  for (const [rawKey, value] of Object.entries(record)) {
    if (PAYLOAD_META_SKIP_KEYS.has(rawKey)) continue;
    if (rawKey === 'role') continue;
    if (ROLE_META_FIELD_ORDER.includes(rawKey as RoleMetaFieldKey)) continue;
    const pair = extractFromToPair(value);
    if (pair) ingestEntry(rawKey, pair.from, pair.to);
  }

  const metaList = record.roleMetaChanges;
  if (Array.isArray(metaList)) {
    for (const entry of metaList) {
      const metaRow = asRecord(entry);
      if (!metaRow) continue;
      const rawKey = String(
        metaRow.fieldName ?? metaRow.field ?? metaRow.key ?? metaRow.name ?? ''
      );
      const pair = extractFromToPair(metaRow);
      if (pair) ingestEntry(rawKey, pair.from, pair.to);
    }
  }

  const nestedRole = asRecord(record.role);
  if (nestedRole) ingestPayloadRecord(nestedRole, roles, byKey);

  const nestedDiff = asRecord(record.diff);
  if (nestedDiff) ingestPayloadRecord(nestedDiff, roles, byKey);

  for (const nestedKey of [
    'changes',
    'roleChanges',
    'meta',
    'fields',
    'attributes'
  ]) {
    const nested = asRecord(record[nestedKey]);
    if (nested) ingestPayloadRecord(nested, roles, byKey);
  }

  ingestBeforeAfterRoot(record, roles, byKey);
};

/** Maps legacy rows where `field` may still be an API key. */
export const mapLegacyRoleMetaRows = (
  rows: { field: string; oldValue: string; newValue: string }[],
  roles: RolePermissionRole[]
): RoleMetaChangeRow[] => {
  const byKey = new Map<RoleMetaFieldKey, RoleMetaChangeRow>();
  const record: Record<string, unknown> = {
    roleMetaChanges: rows.map((row) => ({
      fieldName: row.field,
      oldValue: row.oldValue,
      newValue: row.newValue
    }))
  };
  ingestPayloadRecord(record, roles, byKey);
  return ROLE_META_FIELD_ORDER.filter((key) => byKey.has(key)).map(
    (key) => byKey.get(key)!
  );
};

export const buildRoleMetaChangesFromPayload = (
  payload: Record<string, unknown> | null | undefined,
  roles: RolePermissionRole[],
  previousPayload?: Record<string, unknown> | null
): RoleMetaChangeRow[] => {
  const byKey = new Map<RoleMetaFieldKey, RoleMetaChangeRow>();
  const record = asRecord(payload);
  const previousRecord = asRecord(previousPayload);
  if (record) {
    ingestPayloadRecord(record, roles, byKey);
    ingestSnapshotScalarDiff(record, previousRecord, roles, byKey);
  }
  return ROLE_META_FIELD_ORDER.filter((key) => byKey.has(key)).map(
    (key) => byKey.get(key)!
  );
};

export type HistoryRoleMetaContext = {
  roleCode?: string;
  roleName?: string;
};

/** Build role-meta rows from diffPayload and optional previous history entry. */
export const buildRoleMetaChangesFromHistoryContext = (
  payload: unknown,
  roles: RolePermissionRole[],
  options: {
    previousPayload?: unknown;
    previousItem?: HistoryRoleMetaContext | null;
    currentItem?: HistoryRoleMetaContext | null;
  } = {}
): RoleMetaChangeRow[] => {
  const current = normalizeHistoryPayload(payload);
  const previous = normalizeHistoryPayload(options.previousPayload);
  const byKey = new Map<RoleMetaFieldKey, RoleMetaChangeRow>();

  const mergeRows = (rows: RoleMetaChangeRow[]) => {
    rows.forEach((row) => byKey.set(row.fieldKey, row));
  };

  mergeRows(buildRoleMetaChangesFromPayload(current, roles, previous));

  const upsert = (key: RoleMetaFieldKey, from: unknown, to: unknown) => {
    const row = buildRoleMetaChangeRow(key, from, to, roles);
    if (!row) return;
    byKey.set(key, row);
  };

  const prevItem = options.previousItem;
  const curItem = options.currentItem;
  if (
    prevItem?.roleName &&
    curItem?.roleName &&
    prevItem.roleName.trim() !== curItem.roleName.trim() &&
    !byKey.has('name')
  ) {
    upsert('name', prevItem.roleName, curItem.roleName);
  }
  if (
    prevItem?.roleCode &&
    curItem?.roleCode &&
    prevItem.roleCode.trim() !== curItem.roleCode.trim() &&
    !byKey.has('code')
  ) {
    upsert('code', prevItem.roleCode, curItem.roleCode);
  }

  return ROLE_META_FIELD_ORDER.filter((key) => byKey.has(key)).map(
    (key) => byKey.get(key)!
  );
};

export type ParentChildLinkChangeDisplay = {
  childRoleLabel: string;
  actionLabel: string;
};

/** Parent role log when `parentRoleIds` adds/removes a child (DAIS diffPayload). */
export const buildParentChildLinkChangeDisplay = (
  payload: Record<string, unknown> | null | undefined,
  roles: RolePermissionRole[]
): ParentChildLinkChangeDisplay | null => {
  const record = asRecord(payload);
  if (!record) return null;
  const linkAction = record.action;
  if (linkAction !== 'add' && linkAction !== 'remove') return null;
  const child = asRecord(record.relatedChildRole);
  if (!child) return null;
  const childId = Number(child.id);
  const code = typeof child.code === 'string' ? child.code.trim() : '';
  const name = typeof child.name === 'string' ? child.name.trim() : '';
  let childRoleLabel = code && name ? `${code}（${name}）` : code || name;
  if (!childRoleLabel && Number.isFinite(childId)) {
    childRoleLabel = resolveRoleDisplayName(childId, roles);
  }
  if (!childRoleLabel || childRoleLabel === '—') return null;
  return {
    childRoleLabel,
    actionLabel: linkAction === 'add' ? '追加' : '解除'
  };
};
